import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';

/**
 * ConfirmDialog Component
 * Replaces window.confirm() with a styled MUI dialog for critical actions.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {string} props.title - Dialog title (default: "Confirm Action")
 * @param {string} props.message - Confirmation message
 * @param {string} props.confirmText - Confirm button text (default: "Confirm")
 * @param {string} props.cancelText - Cancel button text (default: "Cancel")
 * @param {string} props.confirmColor - MUI color for confirm button (default: "error")
 * @param {Function} props.onConfirm - Called when user confirms
 * @param {Function} props.onCancel - Called when user cancels
 * @param {boolean} props.loading - Show loading state on confirm button
 *
 * @example
 * const [confirmOpen, setConfirmOpen] = useState(false);
 * const [itemToDelete, setItemToDelete] = useState(null);
 *
 * <ConfirmDialog
 *   open={confirmOpen}
 *   title="Delete Item?"
 *   message="This action cannot be undone."
 *   confirmText="Delete"
 *   onConfirm={async () => { await deleteItem(itemToDelete); setConfirmOpen(false); }}
 *   onCancel={() => setConfirmOpen(false)}
 *   loading={deleting}
 * />
 */
export default function ConfirmDialog({
  open,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'error',
  onConfirm,
  onCancel,
  loading = false,
}) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
